import { Client } from "@searchspring/snap-client";
import { AutocompleteController } from "@searchspring/snap-controller";
import { EventManager } from "@searchspring/snap-event-manager";
import { Logger } from "@searchspring/snap-logger";
import { Profiler } from "@searchspring/snap-profiler";
import { AutocompleteStore } from "@searchspring/snap-store-mobx";
import { Tracker } from "@searchspring/snap-tracker";
import { UrlManager, UrlTranslator } from "@searchspring/snap-url-manager";
import { AutocompleteResponseModel, SearchRequestModelFilterTypeEnum, SearchRequestModelFilterValue, SearchResponseModelResult } from "@searchspring/snapi-types";
import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useState } from "react";
import useContentful from "../hooks/useContentful";

interface SearchContextValue {
  carouselHeadingText: string,
  products: SearchResponseModelResult[];
  searchInputValue: string;
  suggestions: string[];
  updateSearchInput: (value: string) => void;
}

const SearchContext = createContext<SearchContextValue>({
  carouselHeadingText: 'Trending Products',
  products: [],
  searchInputValue: '',
  suggestions: [],
  updateSearchInput: () => {},
});

export const useSearchContext = () => useContext(SearchContext);

interface ProviderProps {
  children?: ReactNode;
  contentfulConfigurationId?: string;
  searchspringSiteId: string;
}

const NUM_SUGGESTIONS_TO_RENDER = 5;

export const SearchContextProvider: FC<ProviderProps> = ({ children, contentfulConfigurationId, searchspringSiteId }) => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [carouselHeadingText, setCarouselHeadingText] = useState('Trending Products');
  const [autocompleteCarouselProducts, setAutocompleteCarouselProducs] = useState<SearchResponseModelResult[]>([]);
  const [contentfulCarouselProducts, setContentfulCarouselProducs] = useState<SearchResponseModelResult[]>([]);
  const [productsToRender, setProductsToRender] = useState<SearchResponseModelResult[]>([]);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<string[]>([]);
  const [contentfulSuggestions, setContentfulSuggestions] = useState<string[]>([]);
  const [trendingSuggestions, setTrendingSuggestions] = useState<string[]>([]);
  const [suggestionsToRender, setSuggestionsToRender] = useState<string[]>([]);

  const { contentfulData, isLoadingContentful } = useContentful({
    'sys.id': contentfulConfigurationId,
    content_type: 'searchConfiguration',
    include: 10,
  });

  const updateSearchInput = (value: string) => setSearchInputValue(value);

  const initAutocompleteController = () => {
    const entryTypeFilter: SearchRequestModelFilterValue = {
      background: true,
      field: 'ss_entry_type',
      type: 'value' as SearchRequestModelFilterTypeEnum,
      value: 'product'
    };
    
    const config = {
      id: 'autocomplete',
      selector: '#searchspring-search-input',
      globals: {
        filters: [entryTypeFilter]
      },
      settings: {
        syncInputs: false,
      }
    };
    const urlManager = new UrlManager(new UrlTranslator()).detach();
    const controller = new AutocompleteController(config, {
      client: new Client({ siteId: searchspringSiteId }),
      store: new AutocompleteStore(config, { urlManager }),
      urlManager,
      eventManager: new EventManager(),
      profiler: new Profiler(),
      logger: new Logger(),
      tracker: new Tracker({ siteId: searchspringSiteId })
    });
    // Initialize and bind to dom element specified in config.selector
    controller.init();
    controller.bind();
    // Handle autocomplete response
    controller.on('afterSearch', async (eventData: { response: AutocompleteResponseModel }, next) => {
      const alternativeObjects = eventData.response.autocomplete?.alternatives || [];
      let alternativeStrings = (alternativeObjects.length > 0) ? alternativeObjects.map(i => i?.text || '') : [];
      setAutocompleteSuggestions(alternativeStrings);
      setAutocompleteCarouselProducs(eventData.response.results || []);
      next();
    });
  }

  const fetchTrendingTerms = () => {
    fetch(`https://${searchspringSiteId}.a.searchspring.io/api/suggest/trending?siteId=${searchspringSiteId}&limit=6`, {
      headers: {accept: 'application/json'}
    }).then(res => res.json()).then(res => {
      const trendingTerms: string[] = [];
      // @ts-ignore
      res.trending.queries.forEach(query => {
        trendingTerms.push(query.searchQuery);
      });
      setTrendingSuggestions(trendingTerms);
    });
  }

  useEffect(() => {
    initAutocompleteController();
  }, []);

  useEffect(() => {
    // If Contentful configuration is found, pull out relevant data
    if (!isLoadingContentful && contentfulData && contentfulData.items?.length > 0) {
      const configurationEntry = contentfulData.items[0];
      // Guard against the wrong content type being entered in theme settings.
      if (configurationEntry.sys.contentType.sys.id == 'searchConfiguration') {
        const { suggestedSearches, trendingProductsCarousel } = configurationEntry.fields;
        setContentfulSuggestions(suggestedSearches as string[] || []);

        // The Trending API only needs hit if there aren't enough Contentful terms.
        // @ts-ignore
        if (suggestedSearches && suggestedSearches.length < NUM_SUGGESTIONS_TO_RENDER) {
          fetchTrendingTerms();
        }

        // @ts-ignore Use carousel tag to fetch products from Searchspring.
        if (trendingProductsCarousel && trendingProductsCarousel.fields.searchspringTags) {
          // @ts-ignore
          const recommendationTags = trendingProductsCarousel.fields.searchspringTags;
          fetch(`https://${searchspringSiteId}.a.searchspring.io/boost/${searchspringSiteId}/recommend?tags=${recommendationTags}&limits=4`, {
            headers: {
              accept: 'application/json'
            }
          }).then(res => res.json()).then(res => {
            setContentfulCarouselProducs(res[0].results);
          });
        }
      }
    }
  }, [isLoadingContentful]);

  // Determine which suggestions should be displayed.
  useEffect(() => {
    let suggestions = [];

    if (searchInputValue && autocompleteSuggestions.length > 0) {
      suggestions = autocompleteSuggestions;
    } else {
      suggestions = contentfulSuggestions;
      // Use Searchspring trending terms if there are not enough from Contentful.
      if (contentfulSuggestions.length < NUM_SUGGESTIONS_TO_RENDER) {
        trendingSuggestions.forEach((term, index) => {
          if (index < NUM_SUGGESTIONS_TO_RENDER - contentfulSuggestions.length) {
            suggestions.push(term);
          }
        });
      }
    }

    setSuggestionsToRender(suggestions);
  }, [autocompleteSuggestions, contentfulSuggestions, trendingSuggestions, searchInputValue]);

  // Determine which products should be displayed in the trending carousel.
  useEffect(() => {
    let products = contentfulCarouselProducts;
    let headingText = 'Trending Products';

    if (searchInputValue && autocompleteCarouselProducts.length > 0) {
      products = autocompleteCarouselProducts;
      headingText = 'Suggested Products';
    }

    setProductsToRender(products);
    setCarouselHeadingText(headingText);
  }, [autocompleteCarouselProducts, contentfulCarouselProducts, searchInputValue]);

  return (
    <SearchContext.Provider value={{
      carouselHeadingText: carouselHeadingText,
      products: productsToRender,
      searchInputValue: searchInputValue,
      suggestions: suggestionsToRender,
      updateSearchInput: updateSearchInput,
    }}>
      {children}
    </SearchContext.Provider>
  );
}